import type {
  AmendmentBillingPayload,
  AppErrors,
  ExecuteAmendmentResponse,
} from '@fitness-force/models';

/**
 * Gym desk payment rule from HOTFIX-FFNXT-API
 * docs/frontend/Amendment-Gym-Options-Frontend-Implementation.md §4.3.
 */
export function requiresGymDeskPayment(
  amendment: AmendmentBillingPayload | null | undefined,
): boolean {
  if (!amendment || amendment.initiated_by !== 'Gym') {
    return false;
  }
  const netDueToday = amendment.net_amount_due_today_in_minor_currency ?? 0;
  return netDueToday > 0 || amendment.new_token_required === true;
}

/**
 * Member / non–gym-desk amendments with nothing due today: skip payment modals and execute directly.
 */
export function shouldCompleteAmendmentWithoutPaymentCollection(
  amendment: AmendmentBillingPayload | null | undefined,
): boolean {
  if (!amendment) {
    return false;
  }
  const netDueToday =
    amendment.net_amount_due_today_in_minor_currency ??
    amendment.net_amount_due_in_minor_currency ??
    0;
  return netDueToday === 0 && !requiresGymDeskPayment(amendment);
}

export function isGymDeskFlowComplete(
  response: ExecuteAmendmentResponse,
  amendment: AmendmentBillingPayload | null | undefined,
): boolean {
  const gymInitiated =
    response.initiated_by === 'Gym' || amendment?.initiated_by === 'Gym';
  const paylinkRequired = response.is_member_paylink_required === true;
  const hasPaylink = !!(response.payment_link_uuid?.trim());
  return gymInitiated && !paylinkRequired && !hasPaylink;
}

export type AmendmentApiErrorCode =
  | 'gym_payment_method_required'
  | 'quote_stale'
  | 'keep_old_enhancement_schedule_unresolved';

export interface AmendmentValidationErrorItem {
  errorCode?: string;
  error_code?: string;
  errorMessage?: string;
  error_message?: string;
}

export function extractAmendmentValidationErrors(
  error?: AppErrors | null,
): AmendmentValidationErrorItem[] {
  const errorData = error?.originalError?.error;
  if (Array.isArray(errorData)) {
    return errorData as AmendmentValidationErrorItem[];
  }
  if (errorData && typeof errorData === 'object') {
    const nested = errorData as { validation_errors?: AmendmentValidationErrorItem[] };
    if (Array.isArray(nested.validation_errors)) {
      return nested.validation_errors;
    }
    return [errorData as AmendmentValidationErrorItem];
  }
  return [];
}

export function getAmendmentErrorCodes(
  error?: AppErrors | null,
): AmendmentApiErrorCode[] {
  return extractAmendmentValidationErrors(error)
    .map((item) => (item.errorCode ?? item.error_code ?? '').toLowerCase())
    .filter((code): code is AmendmentApiErrorCode =>
      code === 'gym_payment_method_required' ||
      code === 'quote_stale' ||
      code === 'keep_old_enhancement_schedule_unresolved',
    );
}

export function resolveAmendmentErrorMessages(
  error: AppErrors,
  translate: (key: string) => string,
): string[] | null {
  const codes = getAmendmentErrorCodes(error);
  if (codes.length === 0) {
    return null;
  }

  const messages = codes.map((code) => {
    switch (code) {
      case 'gym_payment_method_required':
        return translate('AMENDMENT.ERRORS.GYM_PAYMENT_METHOD_REQUIRED');
      case 'quote_stale':
        return translate('AMENDMENT.ERRORS.QUOTE_STALE');
      case 'keep_old_enhancement_schedule_unresolved':
        return translate('AMENDMENT.ERRORS.KEEP_OLD_ENHANCEMENT_UNRESOLVED');
      default:
        return '';
    }
  });

  return messages.filter((message) => message.length > 0);
}
